import { Injectable, inject } from '@angular/core';
import {
  ConstraintTypesV3,
  DatetimeQuestionV3,
  DropdownQuestionV3,
  DescriptionQuestionV3,
  RadioQuestionV3,
  QuestionsV3,
  QuestionV3ControlTypes
} from '@bepatient/common';
import { CoreUser, selectorCoreUser } from '@bepatient/core';
import { Category, selectorEventsCategories } from '@bepatient/events-store';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface EventForm {
  responses: {
    categoryUuid: string;
    datetime: string;
    value: string;
    reminder: string;
  };
  questionPages: QuestionsV3[][];
  color?: string;
}

@Injectable()
export class EventsUtilsService {
  private store = inject<Store<any>>(Store);
  private translate = inject(TranslateService);

  getEventForm(
    readonlyDateTime = false,
    datetime?: string,
    questionValue?: string,
    color?: string
  ): Observable<EventForm> {
    return combineLatest([
      this.store.pipe(
        select(selectorCoreUser),
        filter(user => !!user)
      ),
      this.store.pipe(
        select(selectorEventsCategories),
        filter(categories => !!categories)
      )
    ]).pipe(
      map(([user, categories]) => ({
        responses: {
          categoryUuid: '',
          datetime,
          value: questionValue,
          reminder: ''
        },
        questionPages: this.getFormPages(
          user,
          categories,
          readonlyDateTime,
          datetime,
          questionValue
        ),
        color: color ? color : '#2e2e2e'
      }))
    );
  }

  getCategoryLabel(categoryUuid: string): string {
    let allCategories: Category[];
    this.store
      .pipe(
        select(selectorEventsCategories),
        filter(categories => !!categories)
      )
      .subscribe(categories => (allCategories = categories));

    return (
      allCategories.filter(category => category.uuid === categoryUuid)[0]
        .label || 'event'
    );
  }

  private getFormPages(
    user: CoreUser,
    categories: Category[],
    readonlyDateTime: boolean,
    datetime?: string,
    questionValue?: string
  ): QuestionsV3[][] {
    if (user && user.configuration && categories) {
      const reminderCategories = (categories || []).filter(
        category => category.withReminder
      );

      const questionCategories = (categories || []).filter(
        category => category.question.withQuestion
      );

      const descriptionCategories = (categories || []).filter(
        category =>
          category.descriptionText.length || category.descriptionTitle.length
      );

      const questions: QuestionsV3[] = [
        new DropdownQuestionV3({
          key: 'categoryUuid',
          label: this.translate.instant('EVENTS.FORM.REMINDER_CATEGORY'),
          display: true,
          readonly: readonlyDateTime,
          placeholder: 'EVENTS.FORM.REMINDER_CATEGORY_PLACEHOLDER',
          controlType: QuestionV3ControlTypes.DROPDOWN,
          hideEmptyResponse: true,
          options: categories.map(category => ({
            key: category.label,
            value: category.uuid
          })),
          constraints: [
            {
              type: ConstraintTypesV3.REQUIRED,
              errorMessage: this.translate.instant('EVENTS.ERRORS.REQUIRED')
            }
          ],
          conditionalRules: {
            isConditionnal: false
          }
        }),
        new DatetimeQuestionV3({
          key: 'datetime',
          label: this.translate.instant('EVENTS.FORM.DATETIME'),
          value: datetime,
          display: true,
          placeholder: `${user.configuration.dateSettings.dateFormat} HH:mm`,
          pickerFormat: `${user.configuration.dateSettings.dateFormat} HH:mm`,
          displayFormat: `${user.configuration.dateSettings.dateFormat} HH:mm`,
          mobiDateFormat: 'YYYY-MM-DDTHH:mm:ss',
          constraints: [
            {
              type: ConstraintTypesV3.REQUIRED,
              errorMessage: this.translate.instant('EVENTS.ERRORS.REQUIRED')
            }
          ],
          conditionalRules: {
            isConditionnal: false
          }
        })
      ];

      questionCategories.forEach(category => {
        questions.push(
          new RadioQuestionV3({
            key: 'value',
            value: questionValue,
            label: category.question.title,
            display: true,
            options: questionCategories.length
              ? category.question.options.map(q => ({
                  key: q.value,
                  value: q.name
                }))
              : [],
            constraints: [
              {
                type: ConstraintTypesV3.REQUIRED,
                errorMessage: this.translate.instant('EVENTS.ERRORS.REQUIRED')
              }
            ],
            conditionalRules: {
              isConditionnal: true,
              hasMultipleParentQuestions: true,
              parentQuestions: [
                {
                  name: 'categoryUuid',
                  hasMultipleValuesToSatisfy: false,
                  valuesToBeTruthy: [category.uuid]
                }
              ]
            }
          })
        );
      });

      descriptionCategories.forEach(category => {
        questions.push(
          new DescriptionQuestionV3({
            key: 'description',
            display: true,
            controlType: QuestionV3ControlTypes.DESCRIPTION,
            options: {
              title: category.descriptionTitle,
              text: category.descriptionText
            },
            conditionalRules: {
              isConditionnal: true,
              hasMultipleParentQuestions: true,
              parentQuestions: [
                {
                  name: 'categoryUuid',
                  hasMultipleValuesToSatisfy: false,
                  valuesToBeTruthy: [category.uuid]
                }
              ]
            }
          })
        );
      });

      questions.push(
        new DropdownQuestionV3({
          key: 'reminder',
          label: this.translate.instant('EVENTS.FORM.REMINDER'),
          display: true,
          placeholder: 'EVENTS.FORM.REMINDER_PLACEHOLDER',
          controlType: QuestionV3ControlTypes.DROPDOWN,
          hideEmptyResponse: true,
          options: reminderCategories.length
            ? reminderCategories[0].reminders.map(reminder => ({
                key: reminder.name,
                value: reminder.value
              }))
            : [],
          conditionalRules: {
            isConditionnal: true,
            hasMultipleParentQuestions: true,
            parentQuestions: [
              {
                name: 'categoryUuid',
                hasMultipleValuesToSatisfy: false,
                valuesToBeTruthy: reminderCategories.map(
                  category => category.uuid
                )
              }
            ]
          }
        })
      );
      return [questions];
    } else {
      return [[]];
    }
  }
}
